import * as Tabs from "@radix-ui/react-tabs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, Braces, FileCode2, LoaderCircle, Play, RotateCcw, Sparkles, Upload } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatBytes } from "../../lib/format";
import type { CreateJobProgress, SourceKind } from "../../lib/types";
import { useJobStore } from "../../store/useJobStore";
import { createSvgJob } from "./api";
import { ENGINE_OPTIONS } from "./status";

const accepted = ".tex,.dot,.gv,.d2,.zip";
const stageLabels: Record<CreateJobProgress["stage"], string> = {
  creating: "正在创建私有任务",
  uploading: "正在上传源文件",
  queueing: "正在进入构建队列",
};

function validateSourceName(name: string, mode: SourceKind): string | null {
  const lower = name.toLowerCase();
  if (mode === "zip" && !lower.endsWith(".zip")) return "批量模式必须使用 .zip 文件";
  if (mode !== "zip" && lower.endsWith(".zip")) return "ZIP 文件请切换到批量 ZIP 模式";
  if (![".tex", ".dot", ".gv", ".d2", ".zip"].some((extension) => lower.endsWith(extension))) return "仅支持 .tex、.dot、.gv、.d2 和 .zip";
  return null;
}

function FileDrop({ mode, file, onChange }: { mode: SourceKind; file: File | null; onChange: (file: File | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const hint = mode === "zip" ? "包含多个源码、资源和可选 img.config.json" : "支持 .tex、.dot、.gv、.d2";

  return (
    <div
      className={`dropzone ${dragging ? "dragging" : ""}`}
      onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        onChange(event.dataTransfer.files?.[0] || null);
      }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") inputRef.current?.click(); }}
    >
      {mode === "zip" ? <Archive size={30} /> : <Upload size={30} />}
      <strong>{file?.name || "拖放文件到这里，或点击选择"}</strong>
      <span>{file ? formatBytes(file.size) : hint}</span>
      {file && <button type="button" className="text-button" onClick={(event) => { event.stopPropagation(); onChange(null); }}>移除文件</button>}
      <input ref={inputRef} hidden type="file" accept={mode === "zip" ? ".zip" : accepted} onChange={(event) => onChange(event.target.files?.[0] || null)} />
    </div>
  );
}

export function CreateJobForm({ visualOnly = false }: { visualOnly?: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [clientProgress, setClientProgress] = useState<CreateJobProgress | null>(null);
  const { engine, mode, code, filename, optimize, wrapEditor, setEngine, setMode, setCode, setFilename, setOptimize, loadExample, resetDraft } = useJobStore();
  const engineInfo = useMemo(() => ENGINE_OPTIONS.find((item) => item.value === engine)!, [engine]);

  const mutation = useMutation({
    mutationFn: async () => {
      const source = mode === "code" ? new File([code], filename, { type: "text/plain;charset=utf-8" }) : file;
      if (!source) throw new Error("请选择或粘贴源文件");
      if (source.size === 0) throw new Error("源文件不能为空");
      if (source.size > 20 * 1024 * 1024) throw new Error("源文件不能超过 20 MiB");
      const filenameError = validateSourceName(source.name, mode);
      if (filenameError) throw new Error(filenameError);
      if (visualOnly) return "visual-job";
      return createSvgJob({ engine, sourceKind: mode, source, optimize }, setClientProgress);
    },
    onSuccess: async (jobId) => {
      setClientProgress(null);
      setFile(null);
      toast.success("SVG 任务已提交");
      await queryClient.invalidateQueries({ queryKey: ["svg-jobs"] });
      if (!visualOnly) navigate(`/jobs/${jobId}`);
    },
    onError: (error) => {
      setClientProgress(null);
      toast.error(error instanceof Error ? error.message : "任务提交失败");
    },
  });

  return (
    <section className="panel create-card" data-ui-capture="create-form">
      <div className="section-heading">
        <div><span className="kicker">NEW RENDER</span><h2>创建绘图任务</h2><p>手动确认后才会上传和启动构建。</p></div>
        <button type="button" className="ghost-button" onClick={resetDraft}><RotateCcw size={15} />重置草稿</button>
      </div>

      <div className="form-section">
        <div className="field-heading"><strong>1. 选择渲染引擎</strong><span>{engineInfo.hint}</span></div>
        <div className="engine-grid">
          {ENGINE_OPTIONS.map((item) => (
            <button key={item.value} type="button" className={`engine-card ${engine === item.value ? "selected" : ""}`} onClick={() => setEngine(item.value)}>
              <span className="engine-symbol">{item.shortLabel}</span><strong>{item.label}</strong><small>{item.extension || "自动"}</small>
            </button>
          ))}
        </div>
        <div className="example-row"><span>快速示例</span>{(["tikz", "pgfplots", "graphviz", "d2"] as const).map((value) => <button type="button" key={value} onClick={() => loadExample(value)}>{ENGINE_OPTIONS.find((item) => item.value === value)?.shortLabel}</button>)}</div>
      </div>

      <div className="form-section">
        <div className="field-heading"><strong>2. 添加源码</strong><span>粘贴代码、上传单文件或批量 ZIP</span></div>
        <Tabs.Root value={mode} onValueChange={(value) => { setMode(value as SourceKind); setFile(null); }}>
          <Tabs.List className="tabs-list">
            <Tabs.Trigger value="code"><Braces size={16} />粘贴代码</Tabs.Trigger>
            <Tabs.Trigger value="file"><FileCode2 size={16} />上传文件</Tabs.Trigger>
            <Tabs.Trigger value="zip"><Archive size={16} />批量 ZIP</Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="code" className="tab-content">
            <label className="field-label filename-field"><span>源文件名</span><input value={filename} onChange={(event) => setFilename(event.target.value)} placeholder="diagram.dot" /></label>
            <label className="field-label"><span>绘图源码</span><textarea className={wrapEditor ? "wrap" : ""} value={code} onChange={(event) => setCode(event.target.value)} spellCheck={false} /></label>
            <div className="editor-meta"><span>{new Blob([code]).size.toLocaleString()} 字节</span><code>{engineInfo.label}</code></div>
          </Tabs.Content>
          <Tabs.Content value="file" className="tab-content"><FileDrop mode="file" file={file} onChange={setFile} /></Tabs.Content>
          <Tabs.Content value="zip" className="tab-content"><FileDrop mode="zip" file={file} onChange={setFile} /></Tabs.Content>
        </Tabs.Root>
      </div>

      <div className="form-section options-row">
        <div><strong>3. 输出选项</strong><span>SVG 优化会保留 viewBox、defs、ID 与 marker 兼容性。</span></div>
        <label className="switch-label"><input type="checkbox" checked={optimize} onChange={(event) => setOptimize(event.target.checked)} /><span className="switch" /><strong>启用 SVGO</strong></label>
      </div>

      {clientProgress && <div className="client-progress"><div><LoaderCircle className="spin" size={17} /><span>{stageLabels[clientProgress.stage]}</span><strong>{clientProgress.percent}%</strong></div><div className="progress-track"><span style={{ width: `${clientProgress.percent}%` }} /></div></div>}

      <button className="primary-button submit-button" type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
        {mutation.isPending ? <LoaderCircle className="spin" size={18} /> : <Play size={18} />}生成 SVG
      </button>
      <p className="privacy-note"><Sparkles size={14} />源码进入私有 Storage，不写入 Git 仓库或长期 Artifact。</p>
    </section>
  );
}
